import { SUPPORTED_PROVIDERS } from 'myagent_core'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import './SettingsModal.css'

const DEFAULT_PROVIDER = SUPPORTED_PROVIDERS[0].id
const MODELS_DEBOUNCE_MS = 500

export const LlmBotEditor = ({ bot, api, onSave, onCancel, onDelete, onStartChat }: any) => {
  const isNew = !bot
  const [displayName, setDisplayName] = useState('')
  const [description, setDescription] = useState('')
  const [provider, setProvider] = useState(DEFAULT_PROVIDER)
  const [model, setModel] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [systemPrompt, setSystemPrompt] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  const [models, setModels] = useState<string[]>([])
  const [modelsLoading, setModelsLoading] = useState(false)
  const [modelsError, setModelsError] = useState('')
  const fetchTokenRef = useRef(0)
  const modelListId = useId()

  useEffect(() => {
    if (bot) {
      setDisplayName(bot.displayName || '')
      setDescription(bot.description || '')
      setProvider(bot.llm?.provider || DEFAULT_PROVIDER)
      setModel(bot.llm?.model || '')
      setApiKey(bot.llm?.apiKey || '')
      setSystemPrompt(bot.role?.systemPrompt || '')
    } else {
      setDisplayName('')
      setDescription('')
      setProvider(DEFAULT_PROVIDER)
      setModel('')
      setApiKey('')
      setSystemPrompt('')
    }
    fetchTokenRef.current++
    setModels([])
    setModelsError('')
    setModelsLoading(false)
  }, [bot])

  const fetchModels = useCallback(
    async (p: string, k: string) => {
      if (!api?.listChatModels || !p || !k) return
      const token = ++fetchTokenRef.current
      setModelsLoading(true)
      setModelsError('')
      try {
        const list = await api.listChatModels({ provider: p, apiKey: k })
        if (fetchTokenRef.current !== token) return
        setModels(Array.isArray(list) ? list : [])
      } catch (err: any) {
        console.warn('[LlmBotEditor] listChatModels failed:', err)
        if (fetchTokenRef.current !== token) return
        setModels([])
        setModelsError(err?.message || 'Failed to load models')
      } finally {
        if (fetchTokenRef.current === token) setModelsLoading(false)
      }
    },
    [api],
  )

  useEffect(() => {
    if (!provider || !apiKey) {
      fetchTokenRef.current++ // invalidate any in-flight fetch
      setModels([])
      setModelsError('')
      setModelsLoading(false)
      return
    }
    const handle = setTimeout(() => {
      fetchModels(provider, apiKey)
    }, MODELS_DEBOUNCE_MS)
    return () => {
      clearTimeout(handle)
      fetchTokenRef.current++ // invalidate in-flight fetch from a prior firing
    }
  }, [provider, apiKey, fetchModels])

  const handleSave = async () => {
    if (!displayName.trim()) {
      setError('Name is required')
      return
    }
    setIsSaving(true)
    setError('')
    try {
      await onSave({
        ...(bot ? { botId: bot.botId } : {}),
        displayName: displayName.trim(),
        description: description.trim(),
        llm: {
          provider: provider || null,
          model: model || null,
          apiKey: apiKey || null,
          workerPublicKey: null,
        },
        role: {
          systemPrompt: systemPrompt.trim() || null,
        },
      })
    } catch (err: any) {
      setError(err.message || 'Failed to save')
    } finally {
      setIsSaving(false)
    }
  }

  const modelHint = !apiKey
    ? 'Enter an API Key to load available models'
    : modelsLoading
      ? 'Loading models…'
      : modelsError
        ? `Could not load models (${modelsError}). You can still type a model name.`
        : models.length > 0
          ? `${models.length} models available — type to filter or pick one`
          : ''

  return (
    <div className="settings-panel" data-testid="bot-editor">
      <div className="settings-content">
        <div className="settings-section">
          <h3>{isNew ? 'New Bot' : 'Edit Bot'}</h3>
          <div className="bot-config-field">
            <label className="bot-config-label">Name</label>
            <input
              className="provider-input"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="Code Reviewer"
            />
          </div>
          <div className="bot-config-field" style={{ marginTop: 12 }}>
            <label className="bot-config-label">Description</label>
            <input
              className="provider-input"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Brief description"
            />
          </div>
        </div>

        <div className="settings-section">
          <h3>LLM</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="bot-config-field">
              <label className="bot-config-label">Provider</label>
              <select
                className="provider-input"
                value={provider}
                onChange={e => setProvider(e.target.value)}
              >
                {SUPPORTED_PROVIDERS.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.displayName}
                  </option>
                ))}
              </select>
            </div>
            <div className="bot-config-field">
              <label className="bot-config-label">API Key</label>
              <input
                className="provider-input"
                type="password"
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder="sk-..."
              />
            </div>
            <div className="bot-config-field">
              <label className="bot-config-label">Model</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="provider-input"
                  list={modelListId}
                  value={model}
                  onChange={e => setModel(e.target.value)}
                  placeholder="gpt-4.1, gemini-2.5-flash, etc."
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => fetchModels(provider, apiKey)}
                  disabled={!apiKey || modelsLoading}
                  title="Refresh model list"
                >
                  {modelsLoading ? '…' : 'Refresh'}
                </button>
              </div>
              <datalist id={modelListId}>
                {models.map(m => (
                  <option key={m} value={m} />
                ))}
              </datalist>
              {modelHint && (
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 'var(--font-size-xs, 12px)',
                    color: modelsError ? 'var(--color-error)' : 'var(--text-dim)',
                  }}
                >
                  {modelHint}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="settings-section">
          <h3>System Prompt</h3>
          <textarea
            className="provider-input bot-config-textarea"
            value={systemPrompt}
            onChange={e => setSystemPrompt(e.target.value)}
            placeholder="You are a helpful assistant specialized in..."
            rows={5}
            style={{ resize: 'vertical', minHeight: 80, fontFamily: 'inherit', lineHeight: 1.5 }}
          />
        </div>

        {error && <div className="settings-error">{error}</div>}
      </div>

      <div className="settings-footer">
        {!isNew && onDelete && (
          <button
            className="cancel-button"
            style={{
              color: 'var(--color-error)',
              borderColor: 'var(--color-error)',
              marginRight: 'auto',
            }}
            onClick={() => onDelete(bot.botId)}
          >
            Delete
          </button>
        )}
        {!isNew && onStartChat && (
          <button
            className="save-button"
            style={{ background: 'var(--color-primary)' }}
            onClick={() => onStartChat(bot)}
          >
            Chat
          </button>
        )}
        <button className="cancel-button" onClick={onCancel}>
          Cancel
        </button>
        <button className="save-button" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  )
}
